import CustomAlert from "@/app/components/alerts/CustomAlert";
import { UserAccess } from "@/lib/nextAuth/userPermissions";
import { capAll, capFirst } from "@/lib/strapi/formatters";
import { userHasAccessToSubject } from "@/lib/utils/pageChecks";
import { getLoginUrl } from "@/lib/utils/routing";
import { Anchor, Text, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mui/material";
import { Resources, Subject } from "@tsTypes/subject-schema";
import {
  getUserBillingStatus,
  getUserVerificationStatus,
} from "lib/utils/accessChecks";
import { useSession } from "next-auth/react";
import Link from "next/link";

export default function RestrictedResourceBanner({
  subjectDocumentId,
  subjectName,
  resource,
  userAccess,
}: {
  subjectDocumentId: Subject["documentId"];
  subjectName: Subject["Name"];
  resource: Extract<
    Resources,
    | "topic tests"
    | "cram sheets"
    | "syllabus"
    | "test builder"
    | "mock exams"
    | "test taker"
    | "questions"
    | "videos"
    | "notes"
    | "definitions"
    | "flashcards"
    | "ia examples"
  >;
  userAccess: UserAccess;
}) {
  const { data: session } = useSession();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const membershipStatus = session?.user.subscriptionMeta?.status ?? "none";
  const accountDescriptor = useGetUserAccountDescriptor(
    subjectDocumentId,
    subjectName,
    resource
  );
  const billingStatus = getUserBillingStatus(session);
  const verificationStatus = getUserVerificationStatus(session);

  let ctaMessage = "login";
  let ctaLink = "/login";

  if (userAccess === "LOADING") {
    return (
      <CustomAlert
        message={`Checking your access to ${getResourceActionPhrasing(
          resource
        )}...`}
        visualStyle="subtle"
      />
    );
  }

  if (session) {
    if (!verificationStatus) {
      ctaMessage = "verify your account";
      ctaLink = "/profile/account";
    } else if (billingStatus === "Failed" || membershipStatus === "suspended") {
      ctaMessage = "re-attempt your payment";
      ctaLink = "/profile/membership";
    } else if (membershipStatus === "none") {
      ctaMessage = "join with a membership";
      ctaLink = "/register/membership";
    } else if (membershipStatus === "expired") {
      ctaMessage = "renew your membership";
      ctaLink = "/profile/membership";
    } else if (
      membershipStatus === "trialing" ||
      membershipStatus === "active"
    ) {
      ctaMessage = `upgrade your ${
        membershipStatus === "trialing" ? "trial" : "membership"
      }`;
      ctaLink = "/profile/membership";
    }
  }

  const isLoginCTA = !session && ctaMessage === "login";

  if (userAccess === "Full Access") {
    return (
      <CustomAlert
        message={`You have full access to ${getResourceNicePhrasing(
          resource
        )} with your ${
          membershipStatus === "trialing" ? "trial" : "membership"
        }! Keep sailing through the IB.`}
        visualStyle="success"
        layout={isMobile ? "center" : undefined}
      />
    );
  }

  return (
    <CustomAlert visualStyle="error" layout={isMobile ? "center" : undefined}>
      <Text>
        {session ? (
          <>
            {accountDescriptor}{" "}
            <Anchor
              component={Link}
              href={ctaLink}
              aria-label={capFirst(ctaMessage)}
            >
              {capFirst(ctaMessage)}
            </Anchor>{" "}
            to unlock unlimited access.
          </>
        ) : (
          <>
            Only our members can {getResourceActionPhrasing(resource)}.{" "}
            <Anchor component={Link} href="/register" aria-label="Register">
              Start a free 7 day trial
            </Anchor>{" "}
            or{" "}
            <Anchor
              component={Link}
              href={ctaLink}
              onClick={(event) => {
                if (isLoginCTA) {
                  event.preventDefault();
                  if (typeof window !== "undefined") {
                    window.location.href = getLoginUrl();
                  }
                }
              }}
              aria-label={capFirst(ctaMessage)}
            >
              login
            </Anchor>{" "}
            to unlock access.
          </>
        )}
      </Text>
    </CustomAlert>
  );
}

export function useGetUserAccountDescriptor(
  subjectDocumentId: Subject["documentId"],
  subjectName: Subject["Name"],
  resourceBeingViewed: Parameters<
    typeof RestrictedResourceBanner
  >[0]["resource"]
) {
  let prefix;
  const { data: session, status } = useSession();
  const verificationStatus = getUserVerificationStatus(session);
  const hasAccessToSubject = session
    ? userHasAccessToSubject(session, subjectDocumentId)
    : false;
  if (status === "loading") {
    return undefined;
  } else if (!session) {
    prefix = "As a logged out user, you do not have access to ";
  } else if (!verificationStatus) {
    prefix = "With an unverified account, you do not have access to ";
  } else {
    switch (session.user.subscriptionMeta?.status) {
      case "trialing":
        prefix = `On a trial${
          !hasAccessToSubject ? ` (without ${capAll(subjectName)})` : ""
        }, you do not have access to `;
        break;
      case "expired":
        prefix = "Your membership has expired, so you do not have access to ";
        break;
      case "suspended":
        prefix = "You have a failed payment, so you do not have access to ";
        break;
      case "active":
        prefix = `Your membership does not have ${
          !hasAccessToSubject ? capAll(subjectName) : ""
        } access to `;
        break;
      default:
        prefix = "You have a basic account, so you do not have access to ";
        break;
    }
  }
  return `${prefix} ${getResourceActionPhrasing(resourceBeingViewed)}.`;
}

function getResourceActionPhrasing(
  resource: Extract<
    Resources,
    | "topic tests"
    | "cram sheets"
    | "syllabus"
    | "test builder"
    | "mock exams"
    | "test taker"
    | "questions"
    | "videos"
    | "notes"
    | "definitions"
    | "flashcards"
    | "ia examples"
  >
): string {
  switch (resource) {
    case "topic tests":
      return "take these topic tests";
    case "cram sheets":
      return "download these cram sheets";
    case "syllabus":
      return "tick-off points and update them site-wide";
    case "test builder":
      return "create custom tests or challenge colleagues";
    case "mock exams":
      return "take a mock exam";
    case "test taker":
      return "take custom tests";
    case "questions":
      return "mark these questions";
    case "videos":
      return "watch these videos";
    case "notes":
      return "read these notes";
    case "definitions":
      return "read these definitions";
    case "flashcards":
      return "review these flashcards";
    case "ia examples":
      return "view these IA examples";
  }
}

function getResourceNicePhrasing(
  resource: Extract<
    Resources,
    | "topic tests"
    | "cram sheets"
    | "syllabus"
    | "test builder"
    | "mock exams"
    | "test taker"
    | "questions"
    | "videos"
    | "notes"
    | "definitions"
    | "flashcards"
    | "ia examples"
  >
): string {
  return ["test taker", "test builder", "syllabus"].includes(resource)
    ? `the ${resource}`
    : `these ${resource}`;
}
